import { Alert, Box, Button, Chip, CircularProgress, Paper, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { OperationalFields } from "../types";
import { formatDate } from "../utils/format";
import { useIsInternalReviewer } from "../utils/useIsInternalReviewer";
import { reverifyFacility } from "@/services/vendorProfileService";

type Props = {
  facilityId: string;
  operational: OperationalFields | null | undefined;
};

interface Row {
  label: string;
  value: string;
  color: "success" | "warning" | "error" | "default";
}

const PAN_OK = new Set(["VALID"]);
const GST_OK = new Set(["Active"]);

function panColor(v: string | null | undefined): Row["color"] {
  if (!v) return "default";
  if (PAN_OK.has(v)) return "success";
  return "error";
}

function gstColor(v: string | null | undefined): Row["color"] {
  if (!v) return "default";
  if (GST_OK.has(v)) return "success";
  return "error";
}

export function SystemMonitoringPanel({ facilityId, operational }: Props) {
  const isInternal = useIsInternalReviewer();
  const queryClient = useQueryClient();

  const op = operational ?? {};
  const rows: Row[] = [
    {
      label: "Validation Status",
      value: op.validation_status ?? "—",
      color:
        op.validation_status === "Passed"
          ? "success"
          : op.validation_status
            ? "error"
            : "default",
    },
    {
      label: "Risk Flags",
      value: op.risk_flags?.trim() ? op.risk_flags : "None",
      color: op.risk_flags?.trim() ? "error" : "success",
    },
    {
      label: "Last PAN Verification Date",
      value: formatDate(op.last_pan_verification_date),
      color: "default",
    },
    {
      label: "PAN Status",
      value: op.pan_status ?? "—",
      color: panColor(op.pan_status),
    },
    {
      label: "Last GST Verification Date",
      value: formatDate(op.last_gst_verification_date),
      color: "default",
    },
    {
      label: "GST Status",
      value: op.gst_status ?? "—",
      color: gstColor(op.gst_status),
    },
    {
      label: "Downstream Sync Status",
      value: op.downstream_sync_status ?? "—",
      color: "default",
    },
    {
      label: "Notification Event Status",
      value: op.notification_event_status ?? "—",
      color: "default",
    },
  ];

  const reverifyMutation = useMutation({
    mutationFn: () => reverifyFacility(facilityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor-profile"] });
    },
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 0.8,
            color: "text.secondary",
            mb: 1,
          }}
        >
          SYSTEM-MANAGED OPERATIONAL FIELDS · VIEW ONLY
        </Typography>
        <Paper variant="outlined" sx={{ borderColor: "#E0E4EF", borderRadius: 1 }}>
          {rows.map((r, idx) => (
            <Box
              key={r.label}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                borderBottom: idx === rows.length - 1 ? 0 : 1,
                borderColor: "#E0E4EF",
              }}
            >
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                {r.label}
              </Typography>
              <Chip
                size="small"
                label={r.value}
                color={r.color}
                sx={{ fontWeight: 600, maxWidth: 320 }}
              />
            </Box>
          ))}
        </Paper>
      </Box>

      {isInternal && (
        <Paper
          variant="outlined"
          sx={{ borderColor: "#E0E4EF", borderRadius: 1, p: 3 }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
            Monthly PAN &amp; GST reverification
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
            Runs on the 1st of each month for ACTIVE vendors. A Suspended or
            Cancelled GST result auto-inactivates the vendor and pushes the
            change downstream. Trigger it manually from here.
          </Typography>

          {reverifyMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(reverifyMutation.error as Error)?.message ??
                "Failed to run the monthly job."}
            </Alert>
          )}

          {reverifyMutation.isSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Reverification completed — statuses updated above.
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => reverifyMutation.mutate()}
              disabled={reverifyMutation.isLoading}
              startIcon={
                reverifyMutation.isLoading ? <CircularProgress size={16} /> : undefined
              }
            >
              Run monthly job
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default SystemMonitoringPanel;
